// Catalog.ID Client — Hybrid-KEM keybag chain walk
// ============================================================================
// Shared by every key-grant mode that decapsulates a wrap addressed to one of
// the account's hybrid X25519 + ML-KEM-1024 keys: `decapsulate-efs-encrypt`
// (efs-handlers.ts, keyId `efs_encrypt`) and `decap-chat-epoch`
// (chat-epoch-handlers.ts, keyId `chat_encrypt`).
//
// ONLY the key lookup is shared. Each handler keeps its OWN single-entry HKDF
// `info` allowlist — sharing those would be the domain confusion the separate
// key families exist to prevent (contact-flow.md §5). Lookup is protocol-blind
// arithmetic over public-key derivation, so one implementation is strictly
// better: two copies of "derive the combined pubkey and compare fingerprints"
// is two places for a slicing bug to hide.

import { keys } from '../state/keys';
import { HYBRID_PUBLIC_KEY_LEN, HYBRID_SECRET_KEY_LEN } from '../crypto/kem';
import { x25519 } from '@noble/curves/ed25519.js';
import { ml_kem1024 } from '@noble/post-quantum/ml-kem.js';
import { sha512 } from '@noble/hashes/sha2.js';

/**
 * Walk an unlocked hybrid-KEM key chain (ALL generations, including retiring
 * entries — no depth bound, per the cid #4 design) and return the entry whose
 * derived combined public key SHA-512-matches `digestHex`.
 *
 * Retiring generations matter: a wrap minted before a key rotation is
 * addressed to the older public key, and refusing it would mean a rotation
 * silently destroys everything wrapped before it (EFS package keys; chat
 * conversation history).
 *
 * Public-key derivation:
 *   - x25519_pk = x25519.getPublicKey(secret[0:32])
 *   - mlkem_pk  = ml_kem1024.getPublicKey(secret[32:])
 *   - combined  = x25519_pk(32) || mlkem_pk(1568)
 *   - fingerprint = sha512(combined) → 128-char lowercase hex
 *
 * Returns the matching entry or null. Entries with malformed (non-3200-byte)
 * secrets are skipped rather than aborting the search — they cannot be hybrid
 * KEM keys and should never appear under these key ids, but a defensive skip
 * is cheap and keeps one bad row from hiding a good one.
 */
export function findKemKeyByFingerprint(
    keyId: string,
    digestHex: string,
): { secretKey: Uint8Array; keyIndex: number } | null {
    const chain = keys.getKeyChain(keyId);
    if (chain.length === 0) return null;

    const target = digestHex.toLowerCase();
    for (const entry of chain) {
        if (entry.secretKey.length !== HYBRID_SECRET_KEY_LEN) continue;

        let derivedPublicKey: Uint8Array;
        try {
            derivedPublicKey = deriveKemPublicKey(entry.secretKey);
        } catch {
            // Malformed key bytes — skip rather than abort the whole search.
            continue;
        }

        if (toHexLower(sha512(derivedPublicKey)) === target) {
            return entry;
        }
    }
    return null;
}

/**
 * The combined 1600-byte public key a 3200-byte hybrid secret determines —
 * x25519_pk(32) || mlkem_pk(1568). Throws on a secret of the wrong length or
 * malformed key bytes. Shared by the chain walk above and by the
 * `export-mail-encrypt` handler, which reports each exported generation's
 * public key: one derivation, so the fingerprint a wrap is matched on and the
 * fingerprint an export is stored under can never disagree.
 */
export function deriveKemPublicKey(secretKey: Uint8Array): Uint8Array {
    if (secretKey.length !== HYBRID_SECRET_KEY_LEN) {
        throw new Error(`hybrid KEM secret must be ${HYBRID_SECRET_KEY_LEN} bytes, got ${secretKey.length}`);
    }
    const classicalPk = x25519.getPublicKey(secretKey.slice(0, 32));
    const pqPk = ml_kem1024.getPublicKey(secretKey.slice(32));
    const combined = new Uint8Array(HYBRID_PUBLIC_KEY_LEN);
    combined.set(classicalPk, 0);
    combined.set(pqPk, 32);
    return combined;
}

function toHexLower(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i] as number;
        out += (b < 16 ? '0' : '') + b.toString(16);
    }
    return out;
}
